import { api } from "../api";
import { IS_TELEGRAM } from "../../utils/runtime";

interface TelegramLoginResponse {
  access_token: string;
  user: {
    id: string;
    telegram_id: string;
    username: string | null;
    first_name: string | null;
    last_name: string | null;
  };
}

/**
 * Единая точка получения access-token'а.
 *
 * • Telegram Mini App — берём `initData` и меняем на JWT через
 *   `POST /auth/telegram` (как всегда).
 * • Веб (app.zero-wallet.net) — у нас нет initData. Если в localStorage
 *   уже лежит `access_token`, используем его; иначе фронт должен
 *   отправить юзера на `/auth/login` (это делает RootLayout).
 *   Также пробуем «тихий рефреш» через `POST /auth/web/refresh` —
 *   если refresh-cookie ещё живая (30 дней «запомни меня»), получаем
 *   свежий access и всё, юзер даже не заметил.
 */
export async function getAccessToken(): Promise<string | null> {
  if (IS_TELEGRAM) {
    return getTelegramAccessToken();
  }
  return getWebAccessTokenSilently();
}

async function getTelegramAccessToken(): Promise<string | null> {
  try {
    const initData = window.Telegram?.WebApp?.initData ?? "";
    if (!initData) {
      console.warn("[auth] Telegram initData is empty");
      return null;
    }
    const res = await api.post<TelegramLoginResponse>("/auth/telegram", {
      init_data: initData,
    });
    localStorage.setItem("access_token", res.data.access_token);
    return res.data.access_token;
  } catch (err) {
    console.warn("[auth] getTelegramAccessToken failed", err);
    return null;
  }
}

/**
 * Веб-версия: пробуем тихо обновить access через refresh-cookie. Не удалось —
 * возвращаем `null` и остаёмся в состоянии «нужен логин». Роутинг покажет
 * `/auth/login`.
 */
async function getWebAccessTokenSilently(): Promise<string | null> {
  try {
    const res = await api.post<{
      access_token: string;
      access_expires_at: string;
    }>("/auth/web/refresh");
    localStorage.setItem("access_token", res.data.access_token);
    return res.data.access_token;
  } catch (err) {
    // 401 — cookie нет или истёк, надо логиниться руками. Не спамим consoleerror.
    console.info("[auth] no active web session, need login");
    return null;
  }
}

export function setAccessToken(token: string) {
  localStorage.setItem("access_token", token);
}

export function getStoredToken(): string | null {
  return localStorage.getItem("access_token");
}

export function clearToken() {
  localStorage.removeItem("access_token");
}

/**
 * Полный logout из веб-версии: ревокает сессию на бэке (cookie ⇒ null)
 * и чистит локальный access. В TG-версии просто чистим локальный
 * токен — в mini app «выход» бессмысленный, initData создаст новый.
 */
export async function logout(): Promise<void> {
  if (IS_TELEGRAM) {
    clearToken();
    return;
  }
  try {
    await api.post("/auth/web/logout");
  } catch {
    // ignore — cookie в любом случае очищается на клиенте ниже.
  }
  clearToken();
}
